/**
 * Список текущих админов + bootstrap из ADMIN_EMAILS env-var.
 * Снять с админа — через User detail (или через /admin/users), здесь только просмотр.
 */

import { Alert, Badge, Card, Group, Loader, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchAdmins } from '../../lib/api-admin';
import { fmtDate } from './format';

export function AdminAdminsPage(): JSX.Element {
    const q = useQuery({ queryKey: ['admin-admins'], queryFn: fetchAdmins, retry: false });

    return (
        <Stack gap="md">
            <Title order={2}>Админы</Title>
            <Alert color="blue" variant="light">
                Назначать / снимать с роли «admin» — на странице{' '}
                <Link to="/admin/users">Пользователи</Link>. Здесь — только просмотр.
                Список из env-var <code>ADMIN_EMAILS</code> seedится при каждом старте сервера.
            </Alert>
            {q.isLoading && <Loader />}
            {q.error && <Alert color="red">{(q.error as Error).message}</Alert>}
            {q.data && (
                <Stack gap="lg">
                    <div>
                        <Title order={4} mb="xs">Текущие админы ({q.data.admins.length})</Title>
                        <Card withBorder padding={0}>
                            <Table fz="sm" striped verticalSpacing="sm">
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Email</Table.Th>
                                        <Table.Th>Имя</Table.Th>
                                        <Table.Th>Первый вход</Table.Th>
                                        <Table.Th>Последний вход</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {q.data.admins.map(a => (
                                        <Table.Tr key={a.email}>
                                            <Table.Td><Link to={`/admin/users/${encodeURIComponent(a.email)}`}>{a.email}</Link></Table.Td>
                                            <Table.Td>{a.name ?? '—'}</Table.Td>
                                            <Table.Td><Text fz="xs">{fmtDate(a.firstSeen)}</Text></Table.Td>
                                            <Table.Td><Text fz="xs">{fmtDate(a.lastSeen)}</Text></Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Card>
                    </div>

                    <div>
                        <Title order={4} mb="xs">Bootstrap из <code>ADMIN_EMAILS</code></Title>
                        {q.data.envBootstrap.length === 0
                            ? <Text c="dimmed" fz="sm">не задано — админов добавлять только из UI</Text>
                            : (
                                <Group gap="xs" wrap="wrap">
                                    {q.data.envBootstrap.map(email => (
                                        <Badge key={email} variant="light" size="lg">{email}</Badge>
                                    ))}
                                </Group>
                            )
                        }
                        <Text c="dimmed" fz="xs" mt="xs">
                            При старте сервера эти email'ы автоматически получают роль admin
                            (без удаления того, что назначено из UI).
                        </Text>
                    </div>
                </Stack>
            )}
        </Stack>
    );
}
